import { ArrowDropDown } from '@mui/icons-material';
import { Chip, ListItemText, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';

import { AppDocStatus } from 'api/models/AppDocStatus';
import { DocStatus } from 'data/constants/DocStatus';
import useTranslate from 'hooks/useTranslate';

const STATUS_CONFIG: Record<string, { labelKey: string; color: string; bg: string }> = {
    [AppDocStatus.MISSING]: { labelKey: 'docStatusMissing', color: '#991B1B', bg: '#FEE2E2' },
    [AppDocStatus.FIX_REQUIRED]: {
        labelKey: 'docStatusFixRequired',
        color: '#92400E',
        bg: '#FEF3C7',
    },
    [AppDocStatus.WAITING_FOR_APPROVAL]: {
        labelKey: 'docStatusWaiting',
        color: '#1E40AF',
        bg: '#DBEAFE',
    },
    [AppDocStatus.SIGNATURE_REQUIRED]: {
        labelKey: 'docStatusSignatureRequired',
        color: '#92400E',
        bg: '#FEF3C7',
    },
    [AppDocStatus.PREAPPROVED]: {
        labelKey: 'docStatusPreapproved',
        color: '#065F46',
        bg: '#D1FAE5',
    },
    [AppDocStatus.APPROVED]: { labelKey: 'docStatusApproved', color: '#065F46', bg: '#D1FAE5' },
    [AppDocStatus.AUTO_FILLED]: {
        labelKey: 'docStatusAutoFilled',
        color: '#065F46',
        bg: '#D1FAE5',
    },
    [AppDocStatus.SENT_TO_CLIENT]: {
        labelKey: 'docStatusSentToClient',
        color: '#1E40AF',
        bg: '#DBEAFE',
    },
    [AppDocStatus.CLIENT_COMPLETED]: {
        labelKey: 'docStatusClientCompleted',
        color: '#92400E',
        bg: '#FEF3C7',
    },
    [AppDocStatus.PRE_APPROVED]: {
        labelKey: 'docStatusPreapproved',
        color: '#065F46',
        bg: '#D1FAE5',
    },
};

const STATUS_LIST = Object.values(DocStatus);

interface DocumentStatusChipProps {
    status: string;
    onChangeStatus?: (newStatus: DocStatus) => void;
}

export const getStatusLabelKey = (status: string) => STATUS_CONFIG[status]?.labelKey ?? status;

const DocumentStatusChip = ({ status, onChangeStatus }: DocumentStatusChipProps) => {
    const { translate } = useTranslate();
    const config = STATUS_CONFIG[status] ?? {
        labelKey: status,
        color: 'text.secondary',
        bg: 'action.hover',
    };
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!onChangeStatus) return;
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleSelect = (newStatus: DocStatus) => {
        onChangeStatus?.(newStatus);
        setAnchorEl(null);
    };

    return (
        <>
            <Chip
                size="small"
                label={translate(config.labelKey)}
                deleteIcon={
                    onChangeStatus ? (
                        <ArrowDropDown sx={{ color: `${config.color} !important`, fontSize: 18 }} />
                    ) : undefined
                }
                onDelete={onChangeStatus ? handleClick : undefined}
                onClick={onChangeStatus ? handleClick : undefined}
                sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    color: config.color,
                    backgroundColor: config.bg,
                    height: 24,
                    cursor: onChangeStatus ? 'pointer' : 'default',
                }}
            />
            {onChangeStatus ? (
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{ paper: { sx: { minWidth: 180, mt: 0.5 } } }}
                >
                    {STATUS_LIST.map((item) => (
                        <MenuItem
                            key={item}
                            selected={item === status}
                            onClick={() => handleSelect(item)}
                            sx={{ fontSize: 13 }}
                        >
                            <ListItemText>{translate(getStatusLabelKey(item))}</ListItemText>
                        </MenuItem>
                    ))}
                </Menu>
            ) : null}
        </>
    );
};

export default DocumentStatusChip;
